"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { useState } from "react";
import { Download, CheckCircle, ChevronDown, ChevronUp, Flame, Music, Github, ArrowRight, Star } from "lucide-react";

// Helper function to get rock guitar images for presets
const getGuitarImageForPreset = (presetName: string): string => {
  // Map of preset names to specific rock guitar/concert images
  const imageMap: Record<string, string> = {
    "Classic Rock Tones Bundle": "https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?q=80&w=640&auto=format&fit=crop",
    "Modern Metal Pack": "https://images.unsplash.com/photo-1550291652-6ea9114a47b1?q=80&w=640&auto=format&fit=crop",
    "Hair Metal": "https://images.unsplash.com/photo-1564186763535-ebb21ef5277f?q=80&w=640&auto=format&fit=crop",
    "80s Rock Legends": "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?q=80&w=640&auto=format&fit=crop"
  };
  
  // Return the mapped image or a default rock guitar image
  return imageMap[presetName] || "https://images.unsplash.com/photo-1510915361894-db8b60106cb1?q=80&w=640&auto=format&fit=crop";
};

// Brand and model data
const brandData = [{
  name: "BOSS",
  logo: "/boss-logo.png",
  models: ["BOSS GT1000", "BOSS GX100", "BOSS GX10", "BOSS ME90", "BOSS ME80", "BOSS Katana", "BOSS GT100", "BOSS Pocket GT and GT1"]
}, {
  name: "Line 6",
  logo: "/line6-logo.png",
  models: ["Line 6 Helix", "HX Stomp", "Pod Go", "Express"]
}, {
  name: "Neural DSP",
  logo: "/neural-logo.png",
  models: ["Quad Cortex", "Archetype Series"]
}, {
  name: "Amplitube 5",
  logo: "/amplitube-logo.png",
  models: ["Amplitube 5 MAX", "Amplitube 5 Standard"]
}];

// Featured preset data
const featuredPresets = [{
  name: "Classic Rock Tones Bundle",
  description: "Recreate the iconic sounds of the 70s and 80s rock legends",
  brand: "BOSS",
  model: "GT1000",
  thumbnail: "https://images.unsplash.com/photo-1516924962500-2b4b3b99ea02?q=80&w=640&auto=format&fit=crop"
}, {
  name: "Modern Metal Pack",
  description: "Heavy, tight and articulate tones for modern metal production",
  brand: "Line 6",
  model: "Helix",
  thumbnail: "https://images.unsplash.com/photo-1558098329-a11cff621064?q=80&w=640&auto=format&fit=crop"
}, {
  name: "Hair Metal",
  description: "Big hair, big sounds - the ultimate 80s glam metal tones",
  brand: "Neural DSP",
  model: "Quad Cortex",
  thumbnail: "https://images.unsplash.com/photo-1605020420620-20c943cc4669?q=80&w=640&auto=format&fit=crop"
}, {
  name: "80s Rock Legends",
  description: "Iconic tones from the greatest arena rock bands of the 80s",
  brand: "Amplitube 5",
  model: "MAX",
  thumbnail: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=640&auto=format&fit=crop"
}];
export default function HomePage() {
  const [expandedBrand, setExpandedBrand] = useState<string | null>("BOSS");
  const toggleBrand = (brandName: string) => {
    setExpandedBrand(expandedBrand === brandName ? null : brandName);
  };
  return <div className="min-h-screen bg-zinc-900 text-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex items-center space-x-2">
            <Music className="h-7 w-7 text-purple-500" />
            <span className="text-xl font-bold">
              <span className="text-purple-500">Multi-</span>
              <span className="text-white">Effects</span>
              <span className="text-gray-400">.com</span>
            </span>
          </motion.div>
          
          <div className="hidden md:flex space-x-8 text-sm">
            <a href="#intro" className="hover:text-purple-500 transition-colors">Home</a>
            <a href="#brands" className="hover:text-purple-500 transition-colors">Brands</a>
            <a href="#presets" className="hover:text-purple-500 transition-colors">Presets</a>
            <a href="#" className="hover:text-purple-500 transition-colors">Tutorials</a>
            <a href="#" className="hover:text-purple-500 transition-colors">Contact</a>
          </div>
          
          <button className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-all">
            Sign Up Free
          </button>
        </div>
      </nav>
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image src="https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=2070&auto=format&fit=crop" alt="Rock concert background" fill style={{
          objectFit: "cover",
          objectPosition: "center"
        }} quality={100} />
          <div className="absolute inset-0 bg-black/70 z-10"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-20">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 leading-tight">
              <span className="text-purple-500">Professional Guitar Tones</span>
              <br />At Your Fingertips
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Download premium preset patches for your multi-effects pedals and get stage-ready tones from legendary guitarists and studio professionals.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-purple-600 hover:bg-purple-700 px-8 py-3 rounded-md font-semibold flex items-center gap-2 transition-colors">
                Browse Presets <Download className="h-5 w-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Introduction */}
      <section id="intro" className="py-16 bg-zinc-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="lg:w-1/2">
              <h2 className="text-3xl font-bold mb-6 text-purple-400">High-Quality Presets for Your Multi-Effects Pedal</h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                At Multi-Effects.com, we specialize in crafting professional-grade presets for the most popular multi-effects processors on the market. Our team of sound engineers and professional guitarists have spent countless hours perfecting each tone.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Whether you're looking for vintage classics, modern high-gain, ambient textures, or pristine cleans, our preset library has you covered with instant access to giggable, studio-quality sounds.
              </p>
              
              <div className="flex flex-wrap gap-6 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-500" />
                  <span className="text-gray-200">Studio-Quality Tones</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-500" />
                  <span className="text-gray-200">Regular Updates</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-purple-500" />
                  <span className="text-gray-200">Easy Installation</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="lg:w-1/2">
              <div className="relative h-80 w-full rounded-lg overflow-hidden">
                <Image src="https://images.unsplash.com/photo-1525201548942-d8732f6617a0?q=80&w=1287&auto=format&fit=crop" alt="Electric guitar closeup" fill style={{
                objectFit: "cover"
              }} className="rounded-lg" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Supported Brands Section */}
      <section id="brands" className="py-16 bg-zinc-900">
        <div className="container mx-auto px-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-3">Supported <span className="text-purple-500">Brands & Models</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We develop premium patches and presets for the most popular multi-effects processors and digital modelers on the market.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 gap-6">
            {brandData.map((brand, index) => <motion.div key={brand.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                <div className="p-6 flex justify-between items-center cursor-pointer" onClick={() => toggleBrand(brand.name)}>
                  <div className="flex items-center gap-4">
                    <div className="bg-black p-3 rounded-md">
                      <Music className="h-8 w-8 text-purple-500" />
                    </div>
                    <h3 className="text-xl font-bold">{brand.name}</h3>
                  </div>
                  
                  <div className="flex items-center">
                    <span className="text-sm text-gray-400 mr-4">{brand.models.length} models</span>
                    {expandedBrand === brand.name ? <ChevronUp className="h-5 w-5 text-purple-500" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </div>
                
                {expandedBrand === brand.name && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: "auto"
            }} exit={{
              opacity: 0,
              height: 0
            }} transition={{
              duration: 0.3
            }} className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      {brand.models.map(model => <motion.div key={model} whileHover={{
                  x: 5
                }} className="bg-zinc-700/50 rounded px-4 py-3 flex items-center justify-between hover:bg-zinc-700 transition-colors">
                          <span>{model}</span>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </motion.div>)}
          </div>
        </div>
      </section>
      
      {/* Featured Presets Section */}
      <section id="presets" className="py-16 bg-zinc-800">
        <div className="container mx-auto px-6">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold mb-2">Featured <span className="text-purple-500">Presets</span></h2>
              <p className="text-gray-400">Check out our most popular tone packages</p>
            </div>
            <a href="#" className="text-purple-500 hover:text-purple-400 flex items-center gap-1 font-medium">
              View all <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredPresets.map((preset, index) => <motion.div key={preset.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="bg-zinc-900 rounded-lg overflow-hidden border border-zinc-700 group hover:border-purple-500/50 transition-all">
                <div className="relative h-48 w-full overflow-hidden">
                  <Image 
                    src={getGuitarImageForPreset(preset.name)} 
                    alt={preset.name} 
                    fill 
                    style={{
                      objectFit: "cover"
                    }} 
                    className="group-hover:scale-105 transition-transform duration-500" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 p-4 w-full">
                    <span className="bg-purple-600 text-xs font-bold px-2 py-1 rounded">
                      {preset.brand} {preset.model}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-bold group-hover:text-purple-400 transition-colors">{preset.name}</h3>
                    <div className="flex items-center">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 text-yellow-500 fill-yellow-500" />)}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-gray-400 mb-6 text-sm">{preset.description}</p>
                  
                  {/* Preset card footer - intentionally left empty */}
                  <div className="flex justify-start items-center">
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-16 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <Image src="https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?q=80&w=2070&auto=format&fit=crop" alt="Rock concert background" fill style={{
          objectFit: "cover"
        }} />
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto text-center">
            <div className="inline-block p-2 bg-purple-500/20 rounded-full mb-4">
              <Flame className="h-7 w-7 text-purple-500" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Transform Your Tone?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of guitarists who have upgraded their sound with our premium presets.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} className="bg-transparent hover:bg-white/10 border border-white/30 px-8 py-3 rounded-md font-semibold transition-all">
                Join Newsletter
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between mb-8">
            <div className="mb-8 md:mb-0">
              <div className="flex items-center space-x-2 mb-4">
                <Music className="h-6 w-6 text-purple-500" />
                <span className="text-xl font-bold">
                  <span className="text-purple-500">Multi-</span>
                  <span className="text-white">Effects</span>
                  <span className="text-gray-400">.com</span>
                </span>
              </div>
              <p className="text-gray-400 max-w-xs mb-6">
                Professional guitar tones for your multi-effects pedals and processors.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">
                  <Github className="h-5 w-5" />
                </a>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-white font-semibold mb-4">Brands</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">BOSS</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Line 6</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Neural DSP</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Amplitube</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-4">Resources</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Installation Guides</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Tutorials</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Blog</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Support</a></li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-4">Company</h3>
                <ul className="space-y-2">
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">About Us</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Contact</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Privacy Policy</a></li>
                  <li><a href="#" className="text-gray-400 hover:text-purple-500 transition-colors">Terms of Service</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-t border-zinc-800 pt-8">
            <p className="text-gray-500 text-center">
              © {new Date().getFullYear()} Multi-Effects.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}
